import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import TaskSkeleton from "./TaskSkeleton";
import ExpenseSearchSelect, { mapToSelectOptions } from "./ExpenseSearchSelect";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./ExpenseModule.css";
import {
    FY_MONTHS,
    buildFinancialYearOptions,
    formatDisplayDate,
    formatFinancialYear,
    getFinancialYearStart,
} from "../utils/financialYear";

const NAVY = "#1a237e";
const NAVY_RGB = [26, 35, 126];

const fmt = (v) => Math.round(parseFloat(v || 0)).toLocaleString("en-IN");
const fmtRemaining = (v) => (v === null || v === undefined ? "—" : fmt(v));

const fmtDate = (d) => formatDisplayDate(d);

export default function ExpenseReport() {
    const { http, user } = AuthUser();
    const location = useLocation();
    const [heads, setHeads] = useState([]);
    const [itemMasters, setItemMasters] = useState([]);
    const [report, setReport] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [headId, setHeadId] = useState("");
    const [itemMasterId, setItemMasterId] = useState("");
    const [year, setYear] = useState(getFinancialYearStart());
    const [month, setMonth] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const yearOptions = buildFinancialYearOptions();

    useEffect(() => {
        http.get(`/expense_heads?login_user=${user.id}`)
            .then((res) => { if (res.data.success) setHeads(res.data.data || []); })
            .catch(() => toast.error("Error loading categories"));

        http.get(`/expense_item_masters?login_user=${user.id}`)
            .then((res) => { if (res.data.success) setItemMasters(res.data.data || []); })
            .catch(() => toast.error("Error loading items"));
    }, []);

    const fetchReport = async () => {
        setIsLoading(true);
        try {
            let url = `/expense_report?login_user=${user.id}&year=${year}`;
            if (headId) url += `&head_id=${headId}`;
            if (itemMasterId) url += `&item_master_id=${itemMasterId}`;
            if (month) url += `&month=${month}`;
            if (dateFrom) url += `&date_from=${dateFrom}`;
            if (dateTo) url += `&date_to=${dateTo}`;
            const res = await http.get(url);
            if (res.data.success) {
                setReport(res.data);
            }
        } catch (err) {
            toast.error("Error loading report");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchReport(); }, [location.pathname]);

    const filteredItemMasters = useMemo(() => {
        if (!headId) return itemMasters;
        return itemMasters.filter((item) => String(item.expense_head_id) === String(headId));
    }, [itemMasters, headId]);

    useEffect(() => {
        if (!itemMasterId) return;
        const stillValid = filteredItemMasters.some((item) => String(item.id) === String(itemMasterId));
        if (!stillValid) setItemMasterId("");
    }, [filteredItemMasters, itemMasterId]);

    const clearExtraFilters = () => {
        setHeadId("");
        setItemMasterId("");
        setMonth("");
        setDateFrom("");
        setDateTo("");
    };

    const hasExtraFilters = Boolean(headId || itemMasterId || month || dateFrom || dateTo);

    const exportCsv = () => {
        if (!report?.items?.length) {
            toast.error("No data to export");
            return;
        }
        const headers = ["Date", "Category", "Item", "Qty", "Price", "Amount", "Vendor", "Voucher No", "Note"];
        const rows = report.items.map((item) => [
            fmtDate(item.added_date),
            item.head?.name || "",
            item.item_name || "",
            item.quantity ?? "",
            fmt(item.price),
            fmt(item.amount),
            item.voucher?.vendor?.name || "",
            item.voucher?.voucher_number || "",
            item.description || "",
        ]);
        const csv = [headers, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `Expense_Report_${report.financial_year_label}${month ? `_${report.month_name}` : ""}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success("CSV downloaded");
    };

    const exportPdf = () => {
        if (!report?.items?.length) {
            toast.error("No data to export");
            return;
        }
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pageW = doc.internal.pageSize.getWidth();
        doc.setFontSize(14);
        doc.setTextColor(...NAVY_RGB);
        doc.text("Expense Report", pageW / 2, 14, { align: "center" });
        doc.setFontSize(9);
        doc.setTextColor(80);
        const filterBits = [
            `FY: ${report.financial_year_label}`,
            `Category: ${report.category_name}`,
            `Item: ${report.item_name || "All Items"}`,
            `Month: ${report.month_name}`,
        ];
        if (report.date_from || report.date_to) {
            filterBits.push(`Date: ${report.date_from || "…"} to ${report.date_to || "…"}`);
        }
        doc.text(filterBits.join(" | "), pageW / 2, 20, { align: "center" });
        doc.text(
            `Budget: ${fmt(report.budget)} | Spent: ${fmt(report.spent)} | Remaining: ${fmtRemaining(report.remaining)}`,
            pageW / 2,
            25,
            { align: "center" }
        );

        const body = report.items.map((item, idx) => [
            idx + 1,
            fmtDate(item.added_date),
            item.head?.name || "—",
            item.item_name || "—",
            item.quantity ?? "",
            fmt(item.price),
            fmt(item.amount),
            item.voucher?.vendor?.name || "—",
            item.voucher?.voucher_number || "—",
        ]);

        autoTable(doc, {
            head: [["#", "Date", "Category", "Item", "Qty", "Price", "Amount", "Vendor", "Voucher"]],
            body,
            startY: 30,
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: { fillColor: NAVY_RGB, textColor: 255 },
            columnStyles: { 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" } },
        });

        doc.save(`Expense_Report_${report.financial_year_label}${month ? `_${report.month_name}` : ""}.pdf`);
        toast.success("PDF downloaded");
    };

    const budgetPct = report?.budget > 0 ? Math.min(100, Math.round((report.spent / report.budget) * 100)) : 0;
    const selectedMonthNum = month ? Number(month) : null;

    const categoryOptions = mapToSelectOptions(heads);
    const itemOptions = mapToSelectOptions(filteredItemMasters);
    const yearOptionsList = yearOptions.map((y) => ({ value: String(y), label: formatFinancialYear(y) }));
    const monthOptions = FY_MONTHS.map((m) => ({ value: String(m.value), label: m.label }));

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-3 px-3 pb-4 expense-module">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div>
                        <h5 className="fw-bold mb-0">Expense Report</h5>
                        <small className="text-muted">Category-wise expense with budget comparison (Apr – Mar)</small>
                    </div>
                    {report?.items?.length > 0 && (
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={exportPdf}>
                                <i className="fas fa-file-pdf me-1"></i>PDF
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-success" onClick={exportCsv}>
                                <i className="fas fa-file-csv me-1"></i>CSV
                            </button>
                        </div>
                    )}
                </div>

                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-auto" style={{ minWidth: "170px" }}>
                                <label className="form-label small fw-semibold mb-1">Category</label>
                                <ExpenseSearchSelect
                                    options={categoryOptions}
                                    value={headId}
                                    onChange={setHeadId}
                                    placeholder="Search category..."
                                    isClearable
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "170px" }}>
                                <label className="form-label small fw-semibold mb-1">Item</label>
                                <ExpenseSearchSelect
                                    options={itemOptions}
                                    value={itemMasterId}
                                    onChange={setItemMasterId}
                                    placeholder="Search item..."
                                    isClearable
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "140px" }}>
                                <label className="form-label small fw-semibold mb-1">Financial Year</label>
                                <ExpenseSearchSelect
                                    options={yearOptionsList}
                                    value={String(year)}
                                    onChange={setYear}
                                    placeholder="Search year..."
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "140px" }}>
                                <label className="form-label small fw-semibold mb-1">Month</label>
                                <ExpenseSearchSelect
                                    options={monthOptions}
                                    value={month}
                                    onChange={setMonth}
                                    placeholder="Search month..."
                                    isClearable
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "145px" }}>
                                <label className="form-label small fw-semibold mb-1">From Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={dateFrom}
                                    max={dateTo || undefined}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                />
                            </div>
                            <div className="col-auto" style={{ minWidth: "145px" }}>
                                <label className="form-label small fw-semibold mb-1">To Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={dateTo}
                                    min={dateFrom || undefined}
                                    onChange={(e) => setDateTo(e.target.value)}
                                />
                            </div>
                            <div className="col-auto">
                                <button className="btn btn-sm btn-primary" onClick={fetchReport}>
                                    <i className="fas fa-filter me-1"></i>Filter
                                </button>
                            </div>
                            {hasExtraFilters && (
                                <div className="col-auto">
                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearExtraFilters}>
                                        <i className="fas fa-times me-1"></i>Clear
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {isLoading ? <TaskSkeleton /> : report && (
                    <>
                        <div className="row g-3 mb-3">
                            {[
                                { label: "Financial Year", value: report.financial_year_label, color: NAVY, bg: "#e8eaf6", icon: "fa-calendar" },
                                { label: "Category", value: report.category_name, color: "#1565c0", bg: "#e3f2fd", icon: "fa-tags" },
                                {
                                    label: "Item",
                                    value: report.item_name || "All Items",
                                    color: "#00838f",
                                    bg: "#e0f7fa",
                                    icon: "fa-box",
                                },
                                {
                                    label: month ? "Month Budget" : "Year Budget",
                                    value: fmt(report.budget),
                                    hint: !month && report.year_budget_raw <= 0 && report.monthly_budget_total > 0
                                        ? "From monthly budgets"
                                        : null,
                                    color: "#1565c0",
                                    bg: "#e3f2fd",
                                    icon: "fa-wallet",
                                },
                                {
                                    label: month ? "Month Spent" : "Year Spent",
                                    value: fmt(report.spent),
                                    color: "#ef6c00",
                                    bg: "#fff3e0",
                                    icon: "fa-chart-line",
                                },
                                {
                                    label: "Remaining",
                                    value: fmtRemaining(report.remaining),
                                    color: report.over_budget ? "#c62828" : "#2e7d32",
                                    bg: report.over_budget ? "#ffebee" : "#e8f5e9",
                                    icon: report.over_budget ? "fa-exclamation-triangle" : "fa-piggy-bank",
                                    badge: report.over_budget ? "Over Budget" : null,
                                },
                            ].map((kpi) => (
                                <div className="col-sm-6 col-xl" key={kpi.label}>
                                    <div className="card border-0 shadow-sm h-100" style={{ background: kpi.bg }}>
                                        <div className="card-body py-3 d-flex align-items-center gap-3">
                                            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 40, height: 40, background: "#fff", color: kpi.color }}>
                                                <i className={`fas ${kpi.icon}`}></i>
                                            </div>
                                            <div>
                                                <div className="small text-muted">{kpi.label}</div>
                                                <div className="fw-bold" style={{ color: kpi.color }}>
                                                    {kpi.value}
                                                    {kpi.badge && <span className="badge bg-danger ms-2 small">{kpi.badge}</span>}
                                                </div>
                                                {kpi.hint && <div className="small text-muted">{kpi.hint}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {report.budget > 0 && (
                            <div className="card border-0 shadow-sm mb-3">
                                <div className="card-body py-3">
                                    <div className="d-flex justify-content-between small mb-2">
                                        <span>{report.month_name} — Budget usage</span>
                                        <span>{budgetPct}%</span>
                                    </div>
                                    <div className="progress" style={{ height: "8px" }}>
                                        <div
                                            className={`progress-bar ${report.over_budget ? "bg-danger" : ""}`}
                                            style={{ width: `${budgetPct}%`, background: report.over_budget ? undefined : NAVY }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {report.months?.length > 0 && (() => {
                            const firstHalf = report.months.slice(0, 6);
                            const secondHalf = report.months.slice(6);
                            const renderMonthTable = (rows, title) => (
                                <div className="expense-month-group h-100 mb-0">
                                    <div className="expense-month-group-title">{title}</div>
                                    <div className="table-responsive">
                                        <table className="table table-sm table-hover mb-0 align-middle">
                                            <thead>
                                                <tr style={{ background: "#f5f5f5" }}>
                                                    <th className="ps-2" style={{ minWidth: 90 }}>Month</th>
                                                    <th className="text-end">Budget</th>
                                                    <th className="text-end">Spent</th>
                                                    <th className="text-end pe-2">Rem.</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.map((row) => {
                                                    const pct = row.month_budget > 0
                                                        ? Math.min(100, Math.round((row.spent / row.month_budget) * 100))
                                                        : 0;
                                                    const isSelected = selectedMonthNum === row.month;
                                                    return (
                                                        <tr
                                                            key={row.month}
                                                            className={isSelected ? "table-primary" : row.over_budget ? "table-danger" : ""}
                                                        >
                                                            <td className="ps-2">
                                                                <div className="fw-semibold d-flex align-items-center gap-1">
                                                                    {row.month_name}
                                                                    {isSelected && <span className="badge bg-primary small">On</span>}
                                                                </div>
                                                                {row.month_budget > 0 ? (
                                                                    <div className="d-flex align-items-center gap-1 mt-1">
                                                                        <div className="progress flex-grow-1" style={{ height: 5, minWidth: 56 }}>
                                                                            <div
                                                                                className={`progress-bar ${row.over_budget ? "bg-danger" : ""}`}
                                                                                style={{
                                                                                    width: `${pct}%`,
                                                                                    background: row.over_budget ? undefined : NAVY,
                                                                                }}
                                                                            ></div>
                                                                        </div>
                                                                        <span className="text-muted" style={{ fontSize: "0.65rem", minWidth: 28 }}>
                                                                            {pct}%
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <div className="text-muted mt-1" style={{ fontSize: "0.65rem" }}>—</div>
                                                                )}
                                                            </td>
                                                            <td className="text-end">{fmt(row.month_budget)}</td>
                                                            <td className="text-end">{fmt(row.spent)}</td>
                                                            <td className={`text-end fw-semibold pe-2 ${row.over_budget ? "text-danger" : "text-success"}`}>
                                                                {row.month_budget > 0 ? fmt(row.remaining) : "—"}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );

                            return (
                                <div className="card border-0 shadow-sm mb-3">
                                    <div
                                        className="card-header py-1 px-3 d-flex align-items-center justify-content-between"
                                        style={{ background: NAVY, color: "#fff" }}
                                    >
                                        <span className="fw-semibold" style={{ fontSize: "0.8rem" }}>Monthly</span>
                                        <span style={{ fontSize: "0.68rem", opacity: 0.85 }}>Apr–Mar</span>
                                    </div>
                                    <div className="card-body py-2">
                                        <div className="expense-month-grid">
                                            {renderMonthTable(firstHalf, "Apr – Sep")}
                                            {renderMonthTable(secondHalf, "Oct – Mar")}
                                        </div>
                                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2 px-1 py-2 rounded" style={{ background: "#fff8e1" }}>
                                            <span className="fw-bold small">Total</span>
                                            <div className="d-flex flex-wrap gap-3 small">
                                                <span>Budget: <strong>{fmt(report.monthly_budget_total)}</strong></span>
                                                <span>Spent: <strong>{fmt(report.year_spent)}</strong></span>
                                                <span className={report.year_remaining < 0 ? "text-danger" : "text-success"}>
                                                    Rem.: <strong>{fmtRemaining(report.year_remaining)}</strong>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        <div className="card border-0 shadow-sm">
                            <div
                                className="card-header py-1 px-3 fw-semibold"
                                style={{ background: NAVY, color: "#fff", fontSize: "0.8rem" }}
                            >
                                Expense Lines ({report.total_items})
                            </div>
                            <div className="table-responsive">
                                <table className="table table-sm table-hover mb-0 align-middle">
                                    <thead>
                                        <tr style={{ background: "#f5f5f5" }}>
                                            <th className="ps-3">#</th>
                                            <th>Date</th>
                                            <th>Category</th>
                                            <th>Item</th>
                                            <th className="text-end">Qty</th>
                                            <th className="text-end">Price</th>
                                            <th className="text-end">Amount</th>
                                            <th>Vendor</th>
                                            <th>Voucher</th>
                                            <th className="pe-3">Note</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.items.length === 0 ? (
                                            <tr><td colSpan={10} className="text-center text-muted py-4">No expenses found for selected filters.</td></tr>
                                        ) : report.items.map((item, idx) => (
                                            <tr key={item.id}>
                                                <td className="ps-3 text-muted">{idx + 1}</td>
                                                <td>{fmtDate(item.added_date)}</td>
                                                <td>{item.head?.name || "—"}</td>
                                                <td className="fw-semibold">{item.item_name}</td>
                                                <td className="text-end">{item.quantity}</td>
                                                <td className="text-end">{fmt(item.price)}</td>
                                                <td className="text-end fw-bold">{fmt(item.amount)}</td>
                                                <td>{item.voucher?.vendor?.name || "—"}</td>
                                                <td>{item.voucher?.voucher_number || "—"}</td>
                                                <td className="pe-3">{item.description || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    {report.items.length > 0 && (
                                        <tfoot>
                                            <tr style={{ background: "#fff8e1" }}>
                                                <td colSpan={6} className="text-end fw-bold ps-3 py-2">Total</td>
                                                <td className="text-end fw-bold py-2">{fmt(report.spent)}</td>
                                                <td colSpan={3} className="pe-3"></td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
